import type { AutomationTriggerType } from '@adweb/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { TriggerTypeBadge } from '../../components/ui/TriggerTypeBadge';
import { apiClient } from '../../lib/api-client';

type Status = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'RETIRED';

interface AutomationDetail {
  id: string;
  name: string;
  slug: string;
  category: string | null;
  description: string | null;
  triggerType: AutomationTriggerType;
  status: Status;
  estimatedMinutesSavedPerRun: number | null;
  formUrl: string | null;
  chatUrl: string | null;
  monitoredEmail: string | null;
  scheduleLabel: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  automationId: string | null;
}

/** Préfixe https:// si l'URL n'a pas de schéma. */
function normalizeUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export function EditAutomationModal({ open, onClose, automationId }: Props) {
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: ['automation', automationId],
    queryFn: async () =>
      (await apiClient.get<AutomationDetail>(`/automations/${automationId}`)).data,
    enabled: open && !!automationId,
  });

  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<Status>('DRAFT');
  const [estimated, setEstimated] = useState('');
  const [formUrl, setFormUrl] = useState('');
  const [chatUrl, setChatUrl] = useState('');
  const [monitoredEmail, setMonitoredEmail] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [scheduleLabel, setScheduleLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!detail.data) return;
    const a = detail.data;
    setName(a.name);
    setCategory(a.category ?? '');
    setDescription(a.description ?? '');
    setStatus(a.status);
    setEstimated(
      a.estimatedMinutesSavedPerRun != null ? String(a.estimatedMinutesSavedPerRun) : '',
    );
    setFormUrl(a.formUrl ?? '');
    setChatUrl(a.chatUrl ?? '');
    setMonitoredEmail(a.monitoredEmail ?? '');
    setScheduleLabel(a.scheduleLabel ?? '');
    setWebhookUrl('');
    setWebhookSecret('');
    setError(null);
  }, [detail.data]);

  const triggerType = detail.data?.triggerType;

  const mutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        name,
        category,
        description,
        status,
        ...(estimated ? { estimatedMinutesSavedPerRun: Number(estimated) } : {}),
      };
      if (triggerType === 'FORM' && formUrl) body.formUrl = normalizeUrl(formUrl);
      if (triggerType === 'WEBHOOK') {
        if (webhookUrl) body.webhookUrl = normalizeUrl(webhookUrl);
        if (webhookSecret) body.webhookSecret = webhookSecret;
      }
      if (triggerType === 'SCHEDULED') body.scheduleLabel = scheduleLabel;
      if (triggerType === 'CHAT' && chatUrl) body.chatUrl = normalizeUrl(chatUrl);
      if (triggerType === 'EMAIL' && monitoredEmail) body.monitoredEmail = monitoredEmail.trim();
      return apiClient.patch(`/automations/${automationId}`, body);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['automations'] });
      // Fiche détail (clé distincte) : affiche aussitôt les nouvelles valeurs.
      await qc.invalidateQueries({ queryKey: ['automation'] });
      onClose();
    },
    onError: (err) => {
      const data = (
        err as {
          response?: {
            data?: { message?: string; details?: { fieldErrors?: Record<string, string[]> } };
          };
        }
      ).response?.data;
      const fieldErrors = data?.details?.fieldErrors;
      if (fieldErrors && Object.keys(fieldErrors).length > 0) {
        setError(
          Object.entries(fieldErrors)
            .map(([field, messages]) => `${field} : ${messages.join(', ')}`)
            .join(' • '),
        );
      } else {
        setError(data?.message ?? 'Échec de la mise à jour');
      }
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Modifier l'automatisation"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Annuler
          </Button>
          <Button form="edit-automation-form" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </>
      }
    >
      {detail.isLoading || !detail.data ? (
        <p className="text-sm text-slate-400">Chargement…</p>
      ) : (
        <form id="edit-automation-form" onSubmit={handleSubmit} className="space-y-3" noValidate>
          {error && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
            >
              {error}
            </div>
          )}

          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span className="font-mono">{detail.data.slug}</span>
            <TriggerTypeBadge type={detail.data.triggerType} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="edit-name" className="mb-1 block text-xs font-medium text-slate-700">
                Nom
              </label>
              <input
                id="edit-name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label
                htmlFor="edit-status"
                className="mb-1 block text-xs font-medium text-slate-700"
              >
                Statut
              </label>
              <select
                id="edit-status"
                value={status}
                onChange={(e) => setStatus(e.target.value as Status)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="DRAFT">Brouillon</option>
                <option value="ACTIVE">Active</option>
                <option value="PAUSED">En pause</option>
                <option value="RETIRED">Retirée</option>
              </select>
            </div>
          </div>

          <div>
            <label
              htmlFor="edit-category"
              className="mb-1 block text-xs font-medium text-slate-700"
            >
              Catégorie
            </label>
            <input
              id="edit-category"
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label
              htmlFor="edit-description"
              className="mb-1 block text-xs font-medium text-slate-700"
            >
              Description
            </label>
            <textarea
              id="edit-description"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </div>

          {triggerType === 'FORM' && (
            <div>
              <label
                htmlFor="edit-formUrl"
                className="mb-1 block text-xs font-medium text-slate-700"
              >
                URL du formulaire n8n
              </label>
              <input
                id="edit-formUrl"
                type="url"
                value={formUrl}
                onChange={(e) => setFormUrl(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
          )}

          {triggerType === 'WEBHOOK' && (
            <>
              <div>
                <label
                  htmlFor="edit-webhookUrl"
                  className="mb-1 block text-xs font-medium text-slate-700"
                >
                  Nouvelle URL webhook (laisser vide pour conserver)
                </label>
                <input
                  id="edit-webhookUrl"
                  type="url"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  placeholder="conservée si vide"
                />
              </div>
              <div>
                <label
                  htmlFor="edit-webhookSecret"
                  className="mb-1 block text-xs font-medium text-slate-700"
                >
                  Nouveau secret HMAC (laisser vide pour conserver)
                </label>
                <input
                  id="edit-webhookSecret"
                  type="text"
                  value={webhookSecret}
                  onChange={(e) => setWebhookSecret(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm"
                  placeholder="conservé si vide"
                />
              </div>
            </>
          )}

          {triggerType === 'SCHEDULED' && (
            <div>
              <label
                htmlFor="edit-schedule"
                className="mb-1 block text-xs font-medium text-slate-700"
              >
                Planification (libellé)
              </label>
              <input
                id="edit-schedule"
                type="text"
                value={scheduleLabel}
                onChange={(e) => setScheduleLabel(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
          )}

          {triggerType === 'CHAT' && (
            <div>
              <label
                htmlFor="edit-chatUrl"
                className="mb-1 block text-xs font-medium text-slate-700"
              >
                URL du widget de chat
              </label>
              <input
                id="edit-chatUrl"
                type="url"
                value={chatUrl}
                onChange={(e) => setChatUrl(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="https://votre-site.test/chat"
              />
            </div>
          )}

          {triggerType === 'EMAIL' && (
            <div>
              <label
                htmlFor="edit-monitoredEmail"
                className="mb-1 block text-xs font-medium text-slate-700"
              >
                Adresse e-mail surveillée
              </label>
              <input
                id="edit-monitoredEmail"
                type="email"
                value={monitoredEmail}
                onChange={(e) => setMonitoredEmail(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="factures@entreprise.fr"
              />
            </div>
          )}

          <div>
            <label
              htmlFor="edit-estimated"
              className="mb-1 block text-xs font-medium text-slate-700"
            >
              Temps gagné par exécution (minutes)
            </label>
            <input
              id="edit-estimated"
              type="number"
              min="0"
              step="0.5"
              value={estimated}
              onChange={(e) => setEstimated(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
        </form>
      )}
    </Modal>
  );
}
